// Los tres valores que la tabla de un lanzamiento muestra de cada producto:
// stock, precio y SKU — ADR-2026-09-07-lanzamientos-tabla-de-productos.
//
// No alcanza con leer los campos del producto. Cuando tiene variantes, esos tres
// datos dejan de vivir en el producto y pasan a cada variante (es lo que arma
// `tnBodyDeProducto`), así que mostrar el campo del producto sería mentir: diría
// "sin stock" con 40 unidades repartidas en cuatro talles.
//
// Entonces, con variantes: el stock se suma y se muestra como "Total: N", el
// precio se muestra como rango cuando varía, y el SKU son los de las variantes
// —en Tiendanube el producto no tiene SKU propio; el que se carga arriba es el
// valor por defecto que se copia a cada una y NO se publica—. Los tres devuelven
// además el desglose por variante, que la celda muestra al pasar el mouse.

import type { ProductoDeLanzamiento } from './lanzamientosApi';
import { caminoDe, type CategoriaPlana } from './arbolDeCategorias';
import { etiquetaDeVariante } from './variantes';

/** Lo que se muestra cuando el dato no está cargado. */
export const SIN_DATO = '—';

function aNumero(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Precio listo para leer de un vistazo: con separador de miles y sin ceros de
 * más. Devuelve `null` si no hay precio cargado — la celda decide cómo lo dice.
 */
export function precioCorto(v: string | number | null | undefined): string | null {
  const n = aNumero(v);
  if (n === null) return null;
  return `$${n.toLocaleString('es-AR', { maximumFractionDigits: 2 })}`;
}

/** Una variante en el panel que se abre al pasar el mouse por la celda. */
export interface DetalleDeVariante {
  etiqueta: string;
  stock: string;
  sku: string;
}

export interface ResumenDeFila {
  stock: string;
  precio: string;
  sku: string;
  /**
   * El desglose que la celda muestra al pasar el mouse. Vacío cuando el producto
   * no tiene variantes: ahí el número de la celda ya ES el dato completo y un
   * panel no agregaría nada.
   */
  porVariante: DetalleDeVariante[];
}

/** Un producto tiene variantes de verdad cuando además de los atributos ya cargó alguna. */
function tieneVariantes(p: ProductoDeLanzamiento): boolean {
  return p.atributos.length > 0 && p.variantes.length > 0;
}

/** Suma del stock de las variantes; `null` si ninguna lo definió. */
function stockDeVariantes(p: ProductoDeLanzamiento): number | null {
  const cargados = p.variantes.map((v) => aNumero(v.stock)).filter((n): n is number => n !== null);
  if (cargados.length === 0) return null;
  return cargados.reduce((a, b) => a + b, 0);
}

/**
 * Precio de las variantes. Cada una que no defina el suyo usa el general, que es
 * la misma regla con la que se publica. Un solo valor se muestra solo; varios,
 * como rango.
 */
function precioDeVariantes(p: ProductoDeLanzamiento): string | null {
  const valores = p.variantes
    .map((v) => aNumero(v.precio) ?? aNumero(p.precio))
    .filter((n): n is number => n !== null);
  if (valores.length === 0) return null;
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  return min === max ? precioCorto(min) : `${precioCorto(min)} – ${precioCorto(max)}`;
}

export function resumenDeFila(p: ProductoDeLanzamiento): ResumenDeFila {
  if (!tieneVariantes(p)) {
    return {
      stock: p.stock === null ? SIN_DATO : String(p.stock),
      precio: precioCorto(p.precio) ?? SIN_DATO,
      sku: p.sku?.trim() || SIN_DATO,
      porVariante: [],
    };
  }

  const ordenadas = [...p.variantes].sort((a, b) => a.orden - b.orden);
  const stock = stockDeVariantes(p);
  // El precio solo se separa por variante si está activada esa opción; si no,
  // todas publican el general y mostrar un rango confundiría.
  const precio = p.precioPorVariante ? precioDeVariantes(p) : precioCorto(p.precio);

  // El SKU es SIEMPRE de la variante: en Tiendanube el producto no tiene uno
  // propio, el que se carga arriba es el valor por defecto que se copia a cada
  // una. Así que la celda muestra los de las variantes, no el del producto —
  // que es el que NO se publica.
  const skus = ordenadas.map((v) => v.sku?.trim()).filter((x): x is string => !!x);

  return {
    // "Total:" y no el número pelado: con variantes, el número es una suma de
    // valores que viven en otro lado, y decirlo evita leerlo como el stock de
    // un producto simple.
    stock: stock === null ? SIN_DATO : `Total: ${stock}`,
    precio: precio ?? SIN_DATO,
    sku: skus.length > 0 ? skus.join(', ') : SIN_DATO,
    porVariante: ordenadas.map((v, i) => ({
      etiqueta: etiquetaDeVariante(v, i),
      stock: aNumero(v.stock) === null ? SIN_DATO : String(aNumero(v.stock)),
      sku: v.sku?.trim() || SIN_DATO,
    })),
  };
}

// ── Las columnas de un lanzamiento que exporta carpetas ────────────────────
//
// Ese destino no vende: stock, precio y SKU no existen
// (ADR-2026-09-14-lanzamiento-a-carpetas). Lo que hay que ver de un vistazo es
// lo que decide dónde cae cada foto en el ZIP: en qué categoría está el producto
// y qué variantes tiene.

/** Lo que muestran las dos columnas de un lanzamiento a carpetas. */
export interface ResumenDeCarpetas {
  /** El camino entero, "Remeras › Oversize", que es la ruta de la carpeta. */
  categoria: string;
  /** Los nombres de las variantes, que son las subcarpetas. */
  variantes: string;
}

export function resumenDeCarpetas(
  p: ProductoDeLanzamiento,
  arbol: readonly CategoriaPlana[],
): ResumenDeCarpetas {
  const primera = p.categorias[0];
  // `caminoDe` devuelve vacío si la categoría ya no está en el árbol: es el
  // mismo caso que no tener ninguna, y así lo va a exportar el ZIP.
  const camino = primera ? caminoDe(arbol, primera) : '';
  return {
    categoria: camino || SIN_DATO,
    variantes:
      p.variantes.length === 0
        ? SIN_DATO
        : [...p.variantes]
            .sort((a, b) => a.orden - b.orden)
            .map((v, i) => etiquetaDeVariante(v, i))
            .join(', '),
  };
}
